import React, {
	ChangeEvent,
	FC,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useParams } from "react-router-dom";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import {
	getCardBorderStyle,
	getSurfaceBorderColor,
	getSurfaceBorderStyle,
} from "@/app/application/theme";
import { UserContext, TranslationContext } from "@/app/providers";
import { useUpdateStepStatus } from "@/features/procedures/api/checklist.mutations";
import {
	ProcedureNodeStep,
	SuggestionRequest,
	UpdateStepStatusRequest,
} from "@/features/procedures/model/types/checklist";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import { FormAttachment } from "@/shared/forms";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { AlertToast } from "@/shared/notifications/AlertToast";
import { CloseActionButton, SaveActionButton } from "@/shared/ui";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import {
	Box,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	Stack,
	Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { PostProcedureUpdateSuggestions } from "@/service/ProcedureMutations";
import { useFetchActiveProcedure } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";
import { Language } from "@/model/Language/Language";

dayjs.extend(utc);
dayjs.extend(tz);

interface AddCommentProps {
	checklistId: number | undefined;
	suggestionChecklistId?: number | undefined;
	showAddComment: boolean;
	step: ProcedureNodeStep;
	closeModal: () => void;
	skipComment: boolean;
	skipStep: () => Promise<void>;
	defaultMode?: Mode;
}

type Mode = "suggestion" | "comment";

const MAX_COMMENT_CHARS = 2000;

export const AddComment: FC<AddCommentProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const { id } = useParams<RouteParams>();
	const username = useContext(UserContext)?.Name;
	const { timezone } = useTime();
	const initialMode: Mode =
		props.skipComment ? "comment" : (props.defaultMode ?? "comment");
	const mode = initialMode;

	// form
	const [comment, setComment] = useState("");
	const [file, setFile] = useState<File | undefined>();
	const [fileName, setFileName] = useState<string | undefined>();
	const [taskDate] = useState<Date>(new Date());
	const [selectedTaskGroup, setSelectedTaskGroup] = useState<number>(-1);
	const [commentTouched, setCommentTouched] = useState(false);
	const [submitAttempted, setSubmitAttempted] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const commentInputRef = useRef<HTMLTextAreaElement | null>(null);

	// camera
	const [cameraOpen, setCameraOpen] = useState(false);
	const [cameraLoading, setCameraLoading] = useState(false);
	const [cameraError, setCameraError] = useState<string | null>(null);
	const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([]);
	const [selectedCameraId, setSelectedCameraId] = useState("");
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const streamRef = useRef<MediaStream | null>(null);

	// data
	const { data: procedure } = useFetchActiveProcedure();

	// api
	const updateStepState = useUpdateStepStatus();
	const createProcedureUpdateSuggestion = PostProcedureUpdateSuggestions();

	const dialogTitle = useMemo(
		() =>
			props.skipComment ? (
				<Translate
					String="Procedure.CommentOverlay.AddSkipComment"
					HTMLTag="span"
				>
					Add skip comment
				</Translate>
			) : initialMode === "suggestion" ? (
				<Translate
					String="Procedure.CommentOverlay.AddSuggestion"
					HTMLTag="span"
				>
					Suggest improvement
				</Translate>
			) : (
				<Translate String="Procedure.CommentOverlay.AddComment" HTMLTag="span">
					Add comment
				</Translate>
			),
		[initialMode, props.skipComment],
	);

	const dialogSubtitle = useMemo(
		() =>
			props.skipComment ? (
				<Translate String="Procedure.CommentOverlay.SkipSubtitle">
					Explain why you are skipping this step. This comment will be
					recorded.
				</Translate>
			) : initialMode === "suggestion" ? (
				<Translate String="Procedure.CommentOverlay.SuggestionSubtitle">
					Describe the improvement you want to suggest for this step.
				</Translate>
			) : (
				<Translate String="Procedure.CommentOverlay.Subtitle">
					Add a comment for this step. You can optionally attach an image.
				</Translate>
			),
		[initialMode, props.skipComment],
	);

	const setPickedFile = (f?: File) => {
		setFile(f);
		setFileName(f?.name || undefined);
	};

	/** Camera helpers */
	const stopCamera = () => {
		const s = streamRef.current;
		if (s) {
			s.getTracks().forEach((t) => t.stop());
			streamRef.current = null;
		}
		const v = videoRef.current;
		if (v) {
			try {
				(v as any).srcObject = null;
			} catch {
				v.removeAttribute("src");
			}
		}
		setCameraOpen(false);
		setCameraLoading(false);
	};

	const tryGetStream = async (constraints: MediaStreamConstraints) =>
		navigator.mediaDevices.getUserMedia(constraints);

	const loadCameraDevices = async () => {
		if (!navigator?.mediaDevices?.enumerateDevices) return;
		try {
			const devices = await navigator.mediaDevices.enumerateDevices();
			const videoInputs = devices.filter((d) => d.kind === "videoinput");
			setCameraDevices(videoInputs);

			if (videoInputs.length === 0) {
				setSelectedCameraId("");
				return;
			}

			setSelectedCameraId((prev) => {
				if (prev && videoInputs.some((d) => d.deviceId === prev)) return prev;
				return videoInputs[0].deviceId;
			});
		} catch (err) {
			console.error("Failed to list cameras", err);
		}
	};

	const attachAndPlay = async (stream: MediaStream) => {
		const v = videoRef.current!;
		try {
			(v as any).srcObject = stream;
		} catch {
			v.srcObject = null as any;
			// @ts-ignore
			v.src = URL.createObjectURL(stream);
		}
		v.setAttribute("muted", "true");
		v.setAttribute("playsinline", "true");
		v.setAttribute("autoplay", "true");
		v.muted = true;

		if (v.readyState < 2) {
			await new Promise<void>((resolve) => {
				const onLoaded = () => {
					v.removeEventListener("loadedmetadata", onLoaded);
					resolve();
				};
				v.addEventListener("loadedmetadata", onLoaded, { once: true });
			});
		}
		try {
			await v.play();
		} catch (e) {
			console.warn("video.play() was blocked:", e);
		}
	};

	const waitForVideoReady = async (timeoutMs = 5000) => {
		const v = videoRef.current;
		if (!v) return false;
		if (v.videoWidth > 0 && v.videoHeight > 0) return true;

		return await new Promise<boolean>((resolve) => {
			let resolved = false;
			const cleanup = () => {
				v.removeEventListener("loadedmetadata", onReady);
				v.removeEventListener("canplay", onReady);
			};
			const onReady = () => {
				if (resolved) return;
				if (v.videoWidth > 0 && v.videoHeight > 0) {
					resolved = true;
					cleanup();
					resolve(true);
				}
			};

			v.addEventListener("loadedmetadata", onReady);
			v.addEventListener("canplay", onReady);

			setTimeout(() => {
				if (resolved) return;
				resolved = true;
				cleanup();
				resolve(false);
			}, timeoutMs);
		});
	};

	const startCamera = async (preferredDeviceId?: string) => {
		setCameraError(null);
		setCameraLoading(true);
		stopCamera();

		const preferredId = preferredDeviceId || selectedCameraId;
		const attempts: MediaStreamConstraints[] = [];

		if (preferredId) {
			attempts.push({
				video: {
					deviceId: { exact: preferredId },
					width: { ideal: 1280 },
					height: { ideal: 720 },
				},
				audio: false,
			});
		}

		attempts.push(
			{
				video: {
					facingMode: { ideal: "environment" },
					width: { ideal: 1280 },
					height: { ideal: 720 },
				},
				audio: false,
			},
			{
				video: {
					facingMode: { ideal: "user" },
					width: { ideal: 1280 },
					height: { ideal: 720 },
				},
				audio: false,
			},
			{ video: true, audio: false },
		);

		for (let i = 0; i < attempts.length; i++) {
			try {
				const stream = await tryGetStream(attempts[i]);
				streamRef.current = stream;
				setCameraOpen(true);
				await attachAndPlay(stream);
				const activeTrack = stream.getVideoTracks()?.[0];
				const activeDeviceId = activeTrack?.getSettings?.().deviceId;
				if (activeDeviceId) {
					setSelectedCameraId(activeDeviceId);
				}
				await loadCameraDevices();

				const ok = await waitForVideoReady();
				const trackIsLive = activeTrack?.readyState === "live";

				if (ok || trackIsLive) {
					setCameraLoading(false);
					return;
				} else {
					stopCamera();
				}
			} catch (err) {
				stopCamera();
				if (i === attempts.length - 1) {
					console.error(err);
				}
			}
		}
		setCameraError(
			"Could not start camera preview. Check camera permission, then select another camera and retry.",
		);
		setCameraLoading(false);
	};

	const captureFrame = () => {
		if (!videoRef.current || !canvasRef.current) return;
		const v = videoRef.current;
		const c = canvasRef.current;
		const ctx = c.getContext("2d");
		if (!ctx) return;

		const vw = v.videoWidth || 640;
		const vh = v.videoHeight || 360;
		c.width = vw;
		c.height = vh;
		ctx.drawImage(v, 0, 0, vw, vh);
		c.toBlob((blob) => {
			if (!blob) return;
			const captured = new File([blob], "captured_image.png", {
				type: "image/png",
			});
			setPickedFile(captured);
			AlertToast.success("Photo captured");
		}, "image/png");
		stopCamera();
	};

	/** Files */
	const onFilePick = (e: ChangeEvent<HTMLInputElement>) => {
		const f = e?.target?.files?.[0];
		if (!f) return;
		setPickedFile(f);
	};

	const removeFile = () => setPickedFile(undefined);
	const openFilePicker = () => fileInputRef.current?.click();

	const onDrop = (e: React.DragEvent) => {
		e.preventDefault();
		const f = e.dataTransfer.files?.[0];
		if (!f) return;
		setPickedFile(f);
	};

	/** Save */
	const handleSave = async () => {
		if (isSaving) return;
		setIsSaving(true);
		try {
			if (!props.checklistId) {
				AlertRequest.failed("Active scheme ID is required.");
				return;
			}
			if (!id) {
				AlertRequest.failed("Procedure ID is missing.");
				return;
			}
			const trimmed = comment.trim();
			if (!trimmed) {
				setSubmitAttempted(true);
				AlertRequest.failed("Comment cannot be empty.");
				return;
			}
			if (trimmed.length > MAX_COMMENT_CHARS) {
				AlertRequest.failed("Comment is too long.");
				return;
			}

			if (mode === "comment") {
				const taskGroup =
					typeof selectedTaskGroup === "number" && selectedTaskGroup > 0
						? selectedTaskGroup
						: undefined;
				const request: UpdateStepStatusRequest = {
					ArchiveType: "Comment",
					File: file,
					FileName: fileName,
					ProcedureId: parseInt(id, 10),
					SchemeId: props.checklistId,
					Status: props.step.Status,
					StepId: props.step.Id,
					TaskDate: dayjs(taskDate).tz(timezone).toISOString(),
					TaskGroup: taskGroup,
					Value: trimmed,
					UserName: username,
				};
				await updateStepState(request);
			} else {
				const suggestionChecklistId =
					props.suggestionChecklistId ?? props.checklistId;
				if (!suggestionChecklistId) {
					AlertRequest.failed("Checklist ID is required.");
					return;
				}
				const req: SuggestionRequest = {
					File: file,
					ProcedureId: parseInt(id, 10),
					StepId: props.step.Id,
					ChecklistId: suggestionChecklistId,
					Description: trimmed,
					Anonymous: false,
				};
				await createProcedureUpdateSuggestion(req);
			}

			if (props.skipComment) {
				await props.skipStep();
			}

			AlertToast.success();
			closeDialog();
		} catch (err) {
			console.error(err);
			AlertRequest.failed();
		} finally {
			setIsSaving(false);
		}
	};

	/** Lifecycle */
	const resetForm = () => {
		setComment("");
		setPickedFile(undefined);
		setSelectedTaskGroup(-1);
		setCommentTouched(false);
		setSubmitAttempted(false);
	};

	const closeDialog = () => {
		if (isSaving) return;
		stopCamera();
		resetForm();
		props.closeModal();
	};

	// ESC to close
	useEffect(() => {
		const onEsc = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				closeDialog();
			}
		};
		window.addEventListener("keydown", onEsc);
		return () => window.removeEventListener("keydown", onEsc);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// default mode & task group when opened
	useEffect(() => {
		if (!props.showAddComment) return;

		const updatesGroupId = procedure?.TaskGroups?.find(
			(g) => g.Name === "Procedure updates",
		)?.TaskGroupId;

		setSelectedTaskGroup(
			props.skipComment ? -1 : ((updatesGroupId as number) ?? -1),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.showAddComment, initialMode, procedure, props.skipComment]);

	// focus comment field when dialog opens
	useEffect(() => {
		if (!props.showAddComment) return;
		const t = setTimeout(() => commentInputRef.current?.focus(), 100);
		return () => clearTimeout(t);
	}, [props.showAddComment]);

	// cleanup camera on unmount
	useEffect(() => {
		loadCameraDevices();
		const mediaDevices = navigator?.mediaDevices;
		if (!mediaDevices?.addEventListener) return;

		const onDeviceChange = () => {
			loadCameraDevices();
		};

		mediaDevices.addEventListener("devicechange", onDeviceChange);
		return () =>
			mediaDevices.removeEventListener("devicechange", onDeviceChange);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		return () => stopCamera();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isSaveDisabled =
		isSaving || !comment.trim() || comment.trim().length > MAX_COMMENT_CHARS;

	const showCommentError =
		(commentTouched || submitAttempted) && !comment.trim();
	const showCharCounter =
		comment.length > 0 || comment.length >= 0.8 * MAX_COMMENT_CHARS;
	const previewTagSummaries = (props.step.ListenerTags ?? []).map(
		(listenerTag, index) => ({
			key: listenerTag.Id ?? `${listenerTag.Tag}-${index}`,
			name: listenerTag.Name || listenerTag.Tag,
			value: listenerTag.SavedTagValue ?? listenerTag.CurrentValue,
		}),
	);
	const manualMeasurementEntries = props.step.ManualReadingValues ?? [];
	const measurementHistory = manualMeasurementEntries.slice(-3).reverse();

	return (
		<Dialog
			open={props.showAddComment}
			onClose={closeDialog}
			fullWidth
			maxWidth="md"
			aria-labelledby="add-comment-title"
			PaperProps={{
				sx: {
					borderRadius: 2,
					border: (t) => getCardBorderStyle(t),
					bgcolor: "background.paper",
					overflow: "hidden",
					width: { md: "min(980px, calc(100% - 32px))" },
				},
			}}
		>
			<DialogTitle
				id="add-comment-title"
				component="div"
				sx={{
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
					borderBottom: (t) => getSurfaceBorderStyle(t, "subtle"),
					bgcolor: "background.default",
				}}
			>
				<Typography variant="h6" fontWeight={700} color="text.primary">
					{dialogTitle}
				</Typography>
			</DialogTitle>

			<IconButton
				aria-label={GetLanguageValueAsString(translation, "Auto.Features.Procedures.Ui.Components.Checklists.Checklist.Step.AddComment.L555C16.Close", "close")}
				onClick={closeDialog}
				size="small"
				disabled={isSaving}
				sx={{
					position: "absolute",
					right: 8,
					top: 8,
					color: "text.secondary",
					"&:hover": {
						color: "text.primary",
						bgcolor: "action.hover",
					},
				}}
			>
				<CloseIcon />
			</IconButton>

			<DialogContent
				sx={{
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
					bgcolor: "background.paper",
				}}
			>
				<Stack direction={{ xs: "column", md: "row" }} spacing={2}>
					<Box
						sx={{
							flex: { xs: "1 1 auto", md: "0 0 42%" },
							minWidth: 0,
							alignSelf: "flex-start",
						}}
					>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mb: 2, lineHeight: 1.5 }}
						>
							{dialogSubtitle}
						</Typography>

						<Box
							sx={{
								borderRadius: 2,
								border: (t) => getSurfaceBorderStyle(t, "subtle"),
								bgcolor: "background.paper",
								overflow: "hidden",
								boxShadow: (t) => `0 8px 24px ${t.palette.action.hover}`,
							}}
						>
							<Box
								sx={{
									px: 1.5,
									py: 1.25,
									borderBottom: (t) => getSurfaceBorderStyle(t, "subtle"),
									bgcolor: "background.default",
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									gap: 1,
								}}
							>
								<Typography variant="overline" color="text.secondary">
									<Translate String="Procedure.CommentOverlay.StepContext">
										Relevant step
									</Translate>
								</Typography>
							
							</Box>
							<Box
								sx={{
									p: { xs: 1.5, md: 2 },
									maxHeight: { xs: 260, md: 460 },
									overflowY: "auto",
									color: "text.primary",
									bgcolor: "background.paper",
									lineHeight: 1.7,
									"& .satos-content": {
										margin: 0,
									},
									"& p": {
										margin: 0,
									},
									"& p + p": {
										marginTop: 1.5,
									},
									"& ul, & ol": {
										margin: 0,
										paddingLeft: 3,
									},
									"& li + li": {
										marginTop: 0.75,
									},
									"& strong": {
										fontWeight: 700,
									},
									"& table": {
										width: "100%",
										borderCollapse: "collapse",
										marginTop: 1,
										marginBottom: 1,
									},
									"& th, & td": {
										border: "1px solid",
										borderColor: (t) => getSurfaceBorderColor(t, "subtle"),
										padding: "8px 10px",
									},
									"& button, & [role='button'], & input, & textarea, & select": {
										display: "none !important",
									},
									"& a": {
										color: "inherit",
										textDecoration: "none",
										pointerEvents: "none",
									},
								}}
							>
								<Stack spacing={2}>
							

									<Box
										sx={{
											px: { xs: 0, md: 0.5 },
											py: { xs: 0, md: 0.25 },
										}}
									>
										<SATOSContentRender
											content={
												props.step.Value instanceof Element
													? undefined
													: (props.step.Value as string | undefined)
											}
										/>
									</Box>

									{(props.step.ManualReading ||
										previewTagSummaries.length > 0 ||
										manualMeasurementEntries.length > 0) && (
										<Stack spacing={1.25}>
										

											{previewTagSummaries.length > 0 && (
												<Box
													sx={{
														p: 1.25,
														borderRadius: 1.5,
														bgcolor: "background.default",
														border: (t) =>
															getSurfaceBorderStyle(t, "subtle"),
													}}
												>
													<Typography
														variant="overline"
														color="text.secondary"
														sx={{ display: "block", mb: 0.75 }}
													>
														<Translate String="Procedure.CommentOverlay.LiveTags">
															Tags and live values
														</Translate>
													</Typography>
													<Stack
														direction="row"
														flexWrap="wrap"
														useFlexGap
														spacing={0.75}
													>
														{previewTagSummaries.map((listenerTag) => (
															<Chip
																key={listenerTag.key}
																size="small"
																variant="outlined"
																label={`${listenerTag.name}: ${
																	listenerTag.value ?? "—"
																}`}
																sx={{ maxWidth: "100%" }}
															/>
														))}
													</Stack>
												</Box>
											)}

											{manualMeasurementEntries.length > 0 && (
												<Box
													sx={{
														p: 1.25,
														borderRadius: 1.5,
														bgcolor: "background.default",
														border: (t) =>
															getSurfaceBorderStyle(t, "subtle"),
													}}
												>
													<Typography
														variant="overline"
														color="text.secondary"
														sx={{ display: "block", mb: 0.75 }}
													>
														<Translate String="Procedure.CommentOverlay.MeasurementHistory">
															Recent measurements
														</Translate>
													</Typography>
													<Stack spacing={0.75}>
														{[...measurementHistory].reverse().map((entry, index) => (
																<Box
																	key={`${dayjs.utc(entry.Date)
																		.tz(timezone)
																		.toISOString()}-${entry.Value}-${index}`}
																	sx={{
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "space-between",
																		gap: 1,
																		p: 1,
																		borderRadius: 1,
																		bgcolor: "background.paper",
																	}}
																>
																	<Typography variant="body2" fontWeight={600}>
																		{entry.Value}
																	</Typography>
																	<Typography
																		variant="caption"
																		color="text.secondary"
																	>
																		{dayjs.utc(entry.Date)
																			.tz(timezone)
																			.format("DD/MM/YY HH:mm")}
																	</Typography>
																</Box>
															))}
													</Stack>
												</Box>
											)}
										</Stack>
									)}
								</Stack>
							</Box>
						</Box>
					</Box>

					<Stack spacing={2} sx={{ flex: "1 1 0", minWidth: 0 }}>
						<Box>
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="space-between"
								sx={{ mb: 0.5 }}
							>
								<Typography
									component="label"
									htmlFor="comment"
									variant="body2"
									color="text.secondary"
									sx={{ display: "block", fontWeight: 500 }}
								>
									<Translate String="Procedure.CommentOverlay.Comment">
										{mode === "comment" ? "Comment" : "Write your suggestion"}
									</Translate>
								</Typography>
								{showCharCounter && (
									<Typography
										variant="caption"
										color={
											comment.length >= MAX_COMMENT_CHARS
												? "error"
												: "text.secondary"
										}
									>
										{Math.min(comment.length, MAX_COMMENT_CHARS)} /{" "}
										{MAX_COMMENT_CHARS}
									</Typography>
								)}
							</Stack>

							<TextField
								id="comment"
								name="comment"
								value={comment}
								inputRef={commentInputRef}
								onChange={(e: ChangeEvent<HTMLInputElement>) =>
									setComment(e.target.value.slice(0, MAX_COMMENT_CHARS))
								}
								onBlur={() => setCommentTouched(true)}
								placeholder={
									mode === "comment"
										? "Write a comment about this step…"
										: "Describe your improvement suggestion for this step…"
								}
								multiline
								minRows={5}
								maxRows={12}
								fullWidth
								error={showCommentError}
								helperText={showCommentError ? "Comment cannot be empty." : " "}
								sx={{
									"& .MuiInputBase-root": { borderRadius: 1.5 },
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: (t) => getSurfaceBorderColor(t, "default"),
									},
								}}
							/>
						</Box>

						<Stack spacing={1}>
							<Stack
								direction="row"
								flexWrap="wrap"
								useFlexGap
								spacing={1}
								alignItems="center"
							>
								<input
									ref={fileInputRef}
									hidden
									type="file"
									accept="image/*"
									onChange={onFilePick}
								/>
								<ProcedureButton
									sx={{ textTransform: "none", fontWeight: 600 }}
									size="small"
									variant="outlined"
									startIcon={<FileUploadIcon sx={{ fontSize: 18 }} />}
									onClick={openFilePicker}
								>
									<Translate String="Tasks.TaskView.Comment.Choose.File">
										Choose file
									</Translate>
								</ProcedureButton>
								<ProcedureButton
									sx={{ textTransform: "none", fontWeight: 600 }}
									size="small"
									variant={cameraOpen ? "contained" : "outlined"}
									color="info"
									startIcon={<CameraAltIcon sx={{ fontSize: 18 }} />}
									onClick={() => {
										if (cameraOpen) {
											stopCamera();
											return;
										}
										void startCamera();
									}}
									loading={!cameraOpen && cameraLoading}
									disabled={isSaving}
								>
									{cameraOpen ? (
										<Translate String="Tasks.TaskView.Comment.Capture.Image">
											Capture
										</Translate>
									) : (
										<Translate String="Tasks.TaskView.Comment.Open.Camera">
											Open Camera
										</Translate>
									)}
								</ProcedureButton>
							</Stack>
							{cameraDevices.length > 0 && (
								<TextField
									select
									size="small"
									fullWidth
									label="Camera selection"
									value={selectedCameraId}
									onChange={(e) => {
										const nextDeviceId = e.target.value;
										setSelectedCameraId(nextDeviceId);
										if (cameraOpen) {
											void startCamera(nextDeviceId);
										}
									}}
								>
									{cameraDevices.map((device, index) => (
										<MenuItem key={device.deviceId} value={device.deviceId}>
											{device.label || `Camera ${index + 1}`}
										</MenuItem>
									))}
								</TextField>
							)}

							<Box
								onDragOver={(e) => {
									e.preventDefault();
									e.dataTransfer.dropEffect = "copy";
								}}
								onDrop={onDrop}
								onClick={openFilePicker}
								role="button"
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										openFilePicker();
									}
								}}
								aria-label={GetLanguageValueAsString(translation, "Auto.Features.Procedures.Ui.Components.Checklists.Checklist.Step.AddComment.L812C19.UploadImageDragAndDropOr", "Upload image: drag and drop or click to browse")}
								sx={{
									py: 2,
									px: 1.5,
									textAlign: "center",
									border: "1px dashed",
									borderColor: (t) => getSurfaceBorderColor(t, "strong"),
									borderRadius: 1.5,
									color: "text.secondary",
									cursor: "pointer",
									userSelect: "none",
									typography: "body2",
									transition: "border-color 0.2s, background-color 0.2s",
									"&:hover": {
										borderColor: "primary.main",
										bgcolor: "action.hover",
										color: "text.primary",
									},
									"&:focus-visible": {
										outline: "2px solid",
										outlineOffset: 2,
										outlineColor: "primary.main",
									},
								}}
							>
								<Translate String="Procedure.CommentOverlay.DragDrop">
									Drag & drop an image here, or click to browse
								</Translate>
							</Box>

							{(cameraOpen || cameraLoading || cameraError) && (
								<Box
									sx={{
										mt: 1,
										p: 1,
										border: (t) =>
											`1px dashed ${getSurfaceBorderColor(t, "strong")}`,
										borderRadius: 1,
										bgcolor: "background.paper",
									}}
								>
									{cameraLoading && (
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ p: 2, textAlign: "center" }}
										>
											Starting camera…
										</Typography>
									)}

									{cameraError && (
										<Typography
											variant="body2"
											color="error"
											sx={{ p: 2, textAlign: "center" }}
										>
											{cameraError}
										</Typography>
									)}

									{cameraOpen && !cameraLoading && !cameraError && (
										<>
											<Box
												sx={{
													width: "100%",
													height: 280,
													bgcolor: "grey.900",
													borderRadius: 2,
													overflow: "hidden",
												}}
											>
												<video
													ref={videoRef}
													autoPlay
													playsInline
													muted
													style={{
														width: "100%",
														height: "100%",
														objectFit: "cover",
														display: "block",
													}}
												/>
											</Box>
											<canvas ref={canvasRef} style={{ display: "none" }} />
											<Stack
												direction="row"
												spacing={1}
												justifyContent="flex-end"
												sx={{ mt: 1 }}
											>
												<ProcedureButton
													size="small"
													color="inherit"
													onClick={stopCamera}
													sx={{
														textTransform: "none",
														fontWeight: 800,
														borderRadius: 2,
													}}
												>
													<Translate String="LanguageSettings.Table.Cancel">
														Cancel
													</Translate>
												</ProcedureButton>
												<ProcedureButton
													size="small"
													variant="contained"
													onClick={captureFrame}
													sx={{
														textTransform: "none",
														fontWeight: 800,
														borderRadius: 2,
													}}
												>
													<Translate String="Tasks.TaskView.Comment.Capture.Image">
														Capture
													</Translate>
												</ProcedureButton>
											</Stack>
										</>
									)}
								</Box>
							)}

							{fileName && (
								<FormAttachment
									id={0}
									filename={fileName}
									handleDelete={async () => {
										removeFile();
									}}
								/>
							)}
						</Stack>
					</Stack>
				</Stack>
			</DialogContent>

			<DialogActions
				sx={{
					px: { xs: 2, sm: 2.5 },
					py: 2,
					borderTop: (t) => getSurfaceBorderStyle(t, "subtle"),
					gap: 1.5,
					flexWrap: "wrap",
					bgcolor: "background.paper",
				}}
			>
				<Box sx={{ flex: 1 }} />
				<CloseActionButton
					onClick={closeDialog}
					disabled={isSaving}
					sx={{ fontWeight: 600, borderRadius: 2 }}
				>
					<Translate String="Procedure.Button.Close">Close</Translate>
				</CloseActionButton>
				<SaveActionButton
					onClick={() => {
						void handleSave();
					}}
					loading={isSaving}
					loadingText={
						<Translate String="Procedure.Button.Saving">Saving...</Translate>
					}
					disabled={isSaveDisabled}
					sx={{ fontWeight: 600, borderRadius: 2 }}
				>
					<Translate String="Procedure.Button.Save">Save</Translate>
				</SaveActionButton>
			</DialogActions>
		</Dialog>
	);
};
